import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Send, BellRing, Flame } from "lucide-react";
import { TELEGRAM_URL } from "@/config";
import { EASE } from "@/components/Reveal";
import PhoneMockup from "@/components/PhoneMockup";
import SubscriberCounter from "@/components/SubscriberCounter";

function MaskedLine({ children, delay }) {
    return (
        <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
            <motion.span
                className="block will-change-transform"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 18 });

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            data-testid="hero"
            onMouseMove={onMove}
            className="relative overflow-hidden bg-paper"
        >
            {/* Backdrop */}
            <div className="grid-bg absolute inset-0" />
            <div className="absolute -top-48 left-1/2 h-[560px] w-[min(900px,100vw)] -translate-x-1/2 rounded-full bg-telegram-glow/25 blur-[140px]" />
            <div className="absolute -bottom-40 -right-32 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[120px]" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-16 pt-28 sm:px-6 sm:pt-36 lg:grid-cols-[1.1fr_0.9fr] lg:gap-6 lg:px-8 lg:pb-24 lg:pt-44">
                {/* Copy */}
                <div className="text-center lg:text-left">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="glass inline-flex items-center gap-2.5 rounded-full px-4 py-2 ring-1 ring-ink/5"
                        data-testid="hero-eyebrow"
                    >
                        <span className="pulse-dot h-2 w-2 rounded-full bg-telegram" />
                        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/70 sm:text-[11px]">
                            Canale Telegram · Offerte Amazon
                        </span>
                    </motion.div>

                    <h1 className="mt-7 font-display text-[2.55rem] font-semibold leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-[4.15rem]">
                        <MaskedLine delay={0.15}>
                            Sconti <span className="text-telegram">incredibili.</span>
                        </MaskedLine>
                        <MaskedLine delay={0.3}>Prezzi che</MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="text-telegram-deep">durano poco.</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                        className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg lg:mx-0"
                    >
                        Scopri le migliori offerte Amazon selezionate ogni giorno e ricevile
                        direttamente su Telegram.
                    </motion.p>

                    <SubscriberCounter />

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                        className="mt-9"
                    >
                        <a
                            href={TELEGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-telegram-cta"
                            className="btn-shine group inline-flex items-center gap-3 rounded-full bg-telegram px-7 py-4 font-display text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_40px_-12px_rgba(34,158,217,0.55)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-telegram-deep hover:shadow-[0_24px_50px_-12px_rgba(14,122,184,0.6)] sm:px-9 sm:py-[18px] sm:text-base"
                        >
                            <span aria-hidden>🚀</span>
                            <span>ENTRA NEL CANALE TELEGRAM</span>
                            <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                        <p className="mt-5 text-sm font-medium text-ink/55" data-testid="hero-trust-line">
                            Gratis • Nessun abbonamento • Offerte selezionate ogni giorno
                        </p>
                    </motion.div>
                </div>

                {/* Phone mockup */}
                <motion.div
                    initial={{ opacity: 0, y: 48 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                    className="relative mx-auto w-fit"
                    style={{ perspective: 1200 }}
                >
                    <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
                        <PhoneMockup />

                        {/* Floating chips */}
                        <div className="floaty absolute -right-3 top-10 sm:-right-10">
                            <div
                                data-testid="hero-chip-discount"
                                className="flex items-center gap-2 rounded-2xl bg-white/95 px-4 py-2.5 shadow-[0_16px_40px_-12px_rgba(11,18,32,0.3)] ring-1 ring-ink/5 backdrop-blur"
                            >
                                <Flame className="h-4 w-4 text-flame" />
                                <span className="font-mono text-sm font-bold text-flame">−50%</span>
                            </div>
                        </div>

                        <div className="floaty-slow absolute -left-3 bottom-28 sm:-left-12">
                            <div
                                data-testid="hero-chip-daily"
                                className="flex items-center gap-2 rounded-2xl bg-ink/95 px-4 py-2.5 text-white shadow-[0_16px_40px_-12px_rgba(11,18,32,0.5)] ring-1 ring-white/10 backdrop-blur"
                            >
                                <BellRing className="h-4 w-4 text-telegram-glow" />
                                <span className="text-xs font-semibold">Offerte ogni giorno</span>
                            </div>
                        </div>

                        <div className="floaty absolute -left-4 top-16 hidden sm:block" style={{ animationDelay: "-1.6s" }}>
                            <div className="rounded-full bg-telegram p-3 text-white shadow-[0_14px_30px_-10px_rgba(34,158,217,0.6)]">
                                <Send className="h-4 w-4" />
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
